import ModalLayout from "@/components/layout/ModalLayout";
import { Button } from "@/components/ui/Button";
import DateOneBox from "@/components/ui/DateOneBox";
import SelectBox from "@/components/ui/SelectBox";
import TextAreaBox from "@/components/ui/TextAreaBox";
import { useState, useEffect } from "react";
import { useCreateMemo } from "@/hooks/additional/useMemo";
import { useUserInfo } from "@/hooks/member/useMember";
import { toast } from "@/lib/toast";
import { getToday } from "@/lib/date";

interface AddMemoProps {
  isOpen: boolean;
  onClose: () => void;
  memoToEdit?: {
    id?: number;
    date: string;
    content: string;
    category: string;
    time: string;
    createdBy?: string;
  } | null;
}

export default function AddMemo({ isOpen, onClose, memoToEdit }: AddMemoProps) {
  const { data: userInfo } = useUserInfo();
  const { mutate: saveMemo } = useCreateMemo();
  const isEditMode = !!memoToEdit;

  const [date, setDate] = useState<string>(getToday());
  const [category, setCategory] = useState<string>("");
  const [content, setContent] = useState<string>("");

  // memoToEdit가 변경될 때 폼 데이터 업데이트
  useEffect(() => {
    if (memoToEdit) {
      setDate(memoToEdit.date);
      setCategory(memoToEdit.category);
      setContent(memoToEdit.content);
    } else {
      setDate(getToday());
      setCategory("");
      setContent("");
    }
  }, [memoToEdit, isOpen]);

  // 현재 시간을 HH:mm 형식으로 반환
  const getCurrentTime = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  };

  // 폼 초기화
  const resetForm = () => {
    setDate(getToday());
    setCategory("");
    setContent("");
  };

  // 저장 처리
  const handleSave = () => {
    // 필수 필드 검증
    if (!date || !category || !content.trim()) {
      toast.warning("모든 필수 항목을 입력해주세요.");
      return;
    }

    if (!userInfo?.name) {
      toast.warning("사용자 정보를 찾을 수 없습니다.");
      return;
    }

    const memoData = {
      ...(isEditMode && memoToEdit?.id ? { id: memoToEdit.id } : {}),
      memoDate: date,
      content: content.trim(),
      category: category,
      time: isEditMode && memoToEdit?.time ? memoToEdit.time : getCurrentTime(),
      createdBy: userInfo.name,
    };

    saveMemo(memoData, {
      onSuccess: () => {
        resetForm();
        onClose();
      },
    });
  };

  // 취소 처리
  const handleCancel = () => {
    resetForm();
    onClose();
  };
  return (
    <ModalLayout isOpen={isOpen} onClose={onClose} title={isEditMode ? "메모 수정" : "메모 추가"}>
      <div className="grid grid-cols-2 md:grid-cols-2 gap-3 mb-4">
        <DateOneBox required label="일정 날짜" date={date} onDateChange={setDate} />
        <SelectBox
          required
          label="분류"
          value={category}
          onChange={setCategory}
          options={[
            { label: "중요", value: "중요" },
            { label: "메모", value: "메모" },
          ]}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
        <TextAreaBox
          required
          label="내용"
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="내용을 입력해주세요."
          className="w-full"
          rows={10}
        />
      </div>
      {/* 모달 푸터 */}
      <div className="flex items-center gap-3 mt-6">
        <div className="flex justify-start w-full">
          <Button variant="white" onClick={handleCancel} className=" py-[10px]">
            취소
          </Button>
        </div>
        <div className="flex justify-end w-full">
          <Button variant="primary" onClick={handleSave} className="py-[10px]">
            저장
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}
